import { z } from 'zod';
import { slugSchema } from '../utils/slug';

/** Pauta editorial gerada pela IA. */
export const topicGenerationSchema = z.object({
  topics: z
    .array(
      z.object({
        title: z.string().max(180),
        angle: z.string().max(500),
        primaryKeyword: z.string().max(80),
        secondaryKeywords: z.array(z.string().max(80)).max(8),
        searchIntent: z.enum(['INFORMATIONAL', 'COMMERCIAL', 'TRANSACTIONAL', 'NAVIGATIONAL']),
        funnelStage: z.enum(['TOP', 'MIDDLE', 'BOTTOM']),
        estimatedWordCount: z.number().int(),
        rationale: z.string().max(600),
      }),
    )
    .min(1)
    .max(30),
});
export type TopicGeneration = z.infer<typeof topicGenerationSchema>;

/** Artigo completo gerado pela IA. */
export const articleGenerationSchema = z.object({
  title: z.string().max(180),
  slug: z.string().max(120),
  excerpt: z.string().max(400),
  contentHtml: z.string().max(80000),
  metaTitle: z.string().max(70),
  metaDescription: z.string().max(160),
  categories: z.array(z.string().max(60)).max(5),
  tags: z.array(z.string().max(40)).max(12),
  featuredImagePrompt: z.string().max(1000),
  internalLinkSuggestions: z.array(z.string().max(200)).max(10),
  faq: z
    .array(
      z.object({
        question: z.string().max(240),
        answer: z.string().max(1200),
      }),
    )
    .max(8),
});
export type ArticleGeneration = z.infer<typeof articleGenerationSchema>;

export const generateTopicsSchema = z.object({
  clientId: z.string().uuid(),
  count: z.number().int().min(1).max(30).default(8),
  month: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .optional(),
  themes: z.array(z.string().max(200)).max(10).default([]),
  avoidTitles: z.array(z.string().max(200)).max(50).default([]),
  extraInstructions: z.string().max(2000).optional(),
});
export type GenerateTopicsInput = z.infer<typeof generateTopicsSchema>;

export const generateArticleSchema = z.object({
  clientId: z.string().uuid(),
  topicId: z.string().uuid().optional(),
  title: z.string().max(180).optional(),
  primaryKeyword: z.string().max(80).optional(),
  targetWordCount: z.number().int().min(300).max(4000).default(1200),
  tone: z.enum(['DIDATICO', 'COMERCIAL', 'TECNICO', 'INSTITUCIONAL']).default('DIDATICO'),
  extraInstructions: z.string().max(2000).optional(),
});
export type GenerateArticleInput = z.infer<typeof generateArticleSchema>;

export const contentPostSchema = z.object({
  clientId: z.string().uuid(),
  title: z.string().trim().min(3).max(180),
  slug: slugSchema,
  excerpt: z.string().max(400).optional(),
  contentHtml: z.string().max(200000),
  metaTitle: z.string().max(70).optional(),
  metaDescription: z.string().max(160).optional(),
  categories: z.array(z.string().max(60)).max(8).default([]),
  tags: z.array(z.string().max(40)).max(20).default([]),
  featuredImageKey: z.string().max(500).optional(),
  connectorId: z.string().uuid().optional(),
  scheduledFor: z.coerce.date().optional(),
  topicId: z.string().uuid().optional(),
});
export type ContentPostInput = z.infer<typeof contentPostSchema>;

export const updateContentPostSchema = contentPostSchema.partial().omit({ clientId: true });

export const editorialCalendarSchema = z.object({
  clientIds: z.array(z.string().uuid()).min(1).max(50),
  postsPerMonth: z.number().int().min(1).max(30),
  month: z.string().regex(/^\d{4}-\d{2}$/),
  publishWeekdays: z.array(z.number().int().min(0).max(6)).min(1).max(7).default([2, 4]),
  publishHour: z.number().int().min(0).max(23).default(9),
});
export type EditorialCalendarInput = z.infer<typeof editorialCalendarSchema>;

/** Payload enviado ao conector PHP. */
export interface ConnectorArticlePayload {
  external_id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content_html: string;
  meta_title: string | null;
  meta_description: string | null;
  categories: string[];
  tags: string[];
  featured_image_url: string | null;
  published_at: string;
  author: string | null;
  status: 'publish' | 'draft';
}
